// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { isDefined, isNull, isNullOrUndefined, isUndefined } from '@shared/utils';

export type DateInputFormat = 'mdy' | 'dmy';

export const DATE_INPUT_MASK = '##/##/####';

export const DATE_TIME_INPUT_MASK = '##/##/#### ##:##';

export const DATE_INPUT_PLACEHOLDER_KEY = 'common.date-placeholder';

export const DATE_TIME_INPUT_PLACEHOLDER_KEY = 'common.date-time-placeholder';

const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

const ISO_DATE_TIME_PREFIX =
  /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?/;

const padTwo = (value: number): string => String(value).padStart(2, '0');

export const getDefaultDateInputFormat = (lng: string): DateInputFormat => {
  const language = lng.split('-')[0]?.toLowerCase();

  return language === 'es' ? 'dmy' : 'mdy';
};

export const normalizeDateInput = (
  value: Date | string | null | undefined,
): Date | null => {
  if (isNullOrUndefined(value) || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const isoMatch = ISO_DATE_PREFIX.exec(value);

  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));

    return isValidCalendarDate(
      date.getDate(),
      date.getMonth() + 1,
      date.getFullYear(),
    )
      ? date
      : null;
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const toIsoDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = padTwo(date.getMonth() + 1);
  const day = padTwo(date.getDate());

  return `${year}-${month}-${day}`;
};

export const toIsoDateTimeString = (date: Date, hours = 0, minutes = 0): string =>
  `${toIsoDateString(date)}T${padTwo(hours)}:${padTwo(minutes)}:00`;

export const splitMaskedDateTime = (
  masked: string,
): { dateMasked: string; timeMasked: string } => {
  const trimmed = masked.trim();
  const spaceIndex = trimmed.indexOf(' ');

  if (spaceIndex === -1) {
    return { dateMasked: trimmed, timeMasked: '' };
  }

  return {
    dateMasked: trimmed.slice(0, spaceIndex),
    timeMasked: trimmed.slice(spaceIndex + 1),
  };
};

export const isTimeMaskComplete = (timeMasked: string): boolean => {
  const digits = timeMasked.replace(/\D/g, '');

  return digits.length === 4;
};

export const parseMaskedTime = (
  timeMasked: string,
): { hours: number; minutes: number } | null => {
  if (!isTimeMaskComplete(timeMasked)) return null;

  const digits = timeMasked.replace(/\D/g, '');
  const hours = Number(digits.slice(0, 2));
  const minutes = Number(digits.slice(2, 4));

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  return { hours, minutes };
};

export const formatDateTimeToMask = (
  value: Date | string | null | undefined,
  format: DateInputFormat,
): string => {
  const dateMask = formatDateToMask(value, format);

  if (!dateMask) return '';

  if (typeof value === 'string') {
    const match = ISO_DATE_TIME_PREFIX.exec(value);

    if (isDefined(match) && isDefined(match[4]) && isDefined(match[5])) {
      return `${dateMask} ${match[4]}:${match[5]}`;
    }
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${dateMask} ${padTwo(value.getHours())}:${padTwo(value.getMinutes())}`;
  }

  return `${dateMask} `;
};

export const isDateTimeWithinRange = (
  date: Date,
  hours: number,
  minutes: number,
  minDate?: Date | string | null,
  maxDate?: Date | string | null,
): boolean => {
  const targetDay = toIsoDateString(date);
  const normalizedMin = minDate ? normalizeDateInput(minDate) : null;
  const normalizedMax = maxDate ? normalizeDateInput(maxDate) : null;
  const minDay = normalizedMin ? toIsoDateString(normalizedMin) : null;
  const maxDay = normalizedMax ? toIsoDateString(normalizedMax) : null;

  if (minDay && targetDay < minDay) return false;

  if (maxDay && targetDay > maxDay) return false;

  const target = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
    0,
    0,
  );

  if (maxDay && targetDay === maxDay && target.getTime() > Date.now()) {
    return false;
  }

  if (minDay && targetDay === minDay && normalizedMin) {
    const minBoundary = new Date(
      normalizedMin.getFullYear(),
      normalizedMin.getMonth(),
      normalizedMin.getDate(),
      0,
      0,
      0,
      0,
    );

    if (target.getTime() < minBoundary.getTime()) return false;
  }

  return true;
};

export const resolveMaskedDateTimeValue = (
  masked: string,
  format: DateInputFormat,
  minDate?: Date | string | null,
  maxDate?: Date | string | null,
): string | null => {
  if (!masked.trim()) return '';

  const { dateMasked, timeMasked } = splitMaskedDateTime(masked);

  if (!dateMasked.replace(/\D/g, '')) return '';

  if (!isMaskComplete(dateMasked)) return null;

  const date = parseMaskedDateToDate(dateMasked, format);

  if (!date) return null;

  const timeDigits = timeMasked.replace(/\D/g, '');
  let hours = 0;
  let minutes = 0;

  if (timeDigits.length > 0) {
    if (!isTimeMaskComplete(timeMasked)) return null;

    const parsedTime = parseMaskedTime(timeMasked);

    if (!parsedTime) return null;

    hours = parsedTime.hours;
    minutes = parsedTime.minutes;
  }

  if (!isDateTimeWithinRange(date, hours, minutes, minDate, maxDate)) return null;

  return toIsoDateTimeString(date, hours, minutes);
};

export const formatDateToMask = (
  date: Date | string | null | undefined,
  format: DateInputFormat,
): string => {
  const normalized = normalizeDateInput(date);

  if (!normalized) return '';

  const month = padTwo(normalized.getMonth() + 1);
  const day = padTwo(normalized.getDate());
  const year = String(normalized.getFullYear());

  if (format === 'dmy') {
    return `${day}/${month}/${year}`;
  }

  return `${month}/${day}/${year}`;
};

export const isMaskComplete = (formatted: string): boolean => {
  const digits = formatted.replace(/\D/g, '');

  return digits.length === 8;
};

export const parseMaskedDate = (
  masked: string,
  format: DateInputFormat,
): { day: number; month: number; year: number } | null => {
  if (!isMaskComplete(masked)) return null;

  const digits = masked.replace(/\D/g, '');
  const first = Number(digits.slice(0, 2));
  const second = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4, 8));

  const month = format === 'mdy' ? first : second;
  const day = format === 'mdy' ? second : first;

  return { day, month, year };
};

export const isValidCalendarDate = (
  day: number,
  month: number,
  year: number,
): boolean => {
  if (month < 1 || month > 12 || day < 1 || year < 1) return false;

  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

export const parseMaskedDateToDate = (
  masked: string,
  format: DateInputFormat,
): Date | null => {
  const parts = parseMaskedDate(masked, format);

  if (!parts) return null;

  const { day, month, year } = parts;

  if (!isValidCalendarDate(day, month, year)) return null;

  return new Date(year, month - 1, day);
};

export const isDateWithinRange = (
  date: Date,
  minDate?: Date | string | null,
  maxDate?: Date | string | null,
): boolean => {
  const normalizedMin = normalizeDateInput(minDate);
  const normalizedMax = normalizeDateInput(maxDate);
  const target = toIsoDateString(date);

  if (normalizedMin && target < toIsoDateString(normalizedMin)) return false;

  if (normalizedMax && target > toIsoDateString(normalizedMax)) return false;

  return true;
};

export const resolveMaskedDateValue = (
  masked: string,
  format: DateInputFormat,
  minDate?: Date | string | null,
  maxDate?: Date | string | null,
): string | null => {
  if (!masked.trim()) return '';

  const date = parseMaskedDateToDate(masked, format);

  if (!date) return null;

  if (!isDateWithinRange(date, minDate, maxDate)) return null;

  return toIsoDateString(date);
};
